import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import {
  Alert,
  Anchor,
  Badge,
  Button,
  Center,
  Container,
  Group,
  Loader,
  Paper,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Table,
  Tabs,
  Text,
  Tooltip,
} from '@mantine/core';
import { IconArrowLeft, IconDownload } from '@tabler/icons-react';

import { ContractMeter, LogoPaper, PageTitle } from '@/components';
import { useClientContract, useContractReconciliation } from '@/hooks';
import {
  contractLinesCsv,
  DISCOUNT_REASON_LABELS,
  ITEM_STATUS_LABELS,
  RECON_CLASS_LABELS,
  reconSeverity,
  type ContractDetail,
  type DrawdownLine,
  type ReconClass,
} from '@/types/client-contract';
import { formatMoney } from '@/utils';
import { downloadCsvFile } from '@/utils/downloads';

type LineFilter = 'all' | 'drawn' | 'not-drawn';

const SEVERITY_COLOR = {
  ok: 'green',
  expected: 'gray',
  action: 'orange',
  unexplained: 'red',
} as const;

/** One headline figure: label, value, optional note. */
function StatTile({
  label,
  value,
  note,
}: Readonly<{ label: string; value: string; note?: string }>) {
  return (
    <Paper withBorder p="md">
      <Text size="sm" c="dimmed">
        {label}
      </Text>
      <Text size="xl" fw={600}>
        {value}
      </Text>
      {note ? (
        <Text size="xs" c="dimmed">
          {note}
        </Text>
      ) : null}
    </Paper>
  );
}

function percentText(rate: number): string {
  return `${(rate * 100).toLocaleString('en-GB', { maximumFractionDigits: 2 })}%`;
}

function LineStatus({ line }: Readonly<{ line: DrawdownLine }>) {
  if (line.status !== 'eligible') {
    return (
      <Badge size="sm" variant="light" color="gray">
        {ITEM_STATUS_LABELS[line.status]}
      </Badge>
    );
  }
  return <Text size="sm">{line.reason ? DISCOUNT_REASON_LABELS[line.reason] : 'Drawn'}</Text>;
}

function LinesTab({ contract }: Readonly<{ contract: ContractDetail }>) {
  const [filter, setFilter] = useState<LineFilter>('all');
  const money = (value: number | null) => formatMoney(value, contract.currency) ?? '—';
  const lines = contract.lines.filter((line) => {
    if (filter === 'drawn') {
      return line.status === 'eligible';
    }
    return filter === 'not-drawn' ? line.status !== 'eligible' : true;
  });

  return (
    <Stack>
      <Group justify="space-between">
        <SegmentedControl
          value={filter}
          onChange={(value) => setFilter(value as LineFilter)}
          data={[
            { value: 'all', label: `All (${contract.lines.length})` },
            { value: 'drawn', label: 'Drawn' },
            { value: 'not-drawn', label: 'Not drawn' },
          ]}
        />
        <Button
          variant="outline"
          leftSection={<IconDownload size={16} />}
          onClick={() =>
            downloadCsvFile(
              contractLinesCsv(contract),
              `contract-${contract.backofficeContractId}-lines.csv`
            )
          }
        >
          Download CSV
        </Button>
      </Group>
      {lines.length === 0 ? (
        <Text size="sm" c="dimmed" ta="center" py="lg">
          No work in this window yet.
        </Text>
      ) : (
        <Table highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Date</Table.Th>
              <Table.Th>Item</Table.Th>
              <Table.Th>Client case no.</Table.Th>
              <Table.Th>Project ID</Table.Th>
              <Table.Th ta="right">Amount</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th ta="right">Discount</Table.Th>
              <Table.Th ta="right">Net</Table.Th>
              <Table.Th ta="right">Overage</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {lines.map((line) => {
              const crossing = line.itemId === contract.crossingItemId;
              return (
                <Table.Tr
                  key={line.itemId}
                  data-testid={`line-${line.itemId}`}
                  bg={crossing ? 'var(--mantine-color-navy-0)' : undefined}
                >
                  <Table.Td>{line.consumedAt.slice(0, 10)}</Table.Td>
                  <Table.Td>
                    {line.reportId ? (
                      <Anchor component={Link} to={`/reports/${line.reportId}`} size="sm">
                        {line.label ?? 'Report'}
                      </Anchor>
                    ) : (
                      <Text size="sm">{line.label ?? `Project ${line.sourceRef}`}</Text>
                    )}
                    {crossing ? (
                      <Text size="xs" c="dimmed">
                        Crossed the commitment
                      </Text>
                    ) : null}
                  </Table.Td>
                  <Table.Td>{line.clientReference ?? '—'}</Table.Td>
                  <Table.Td>{line.backofficeProjectId ?? '—'}</Table.Td>
                  <Table.Td ta="right">
                    {line.amount === null ? '—' : formatMoney(line.amount, line.currency)}
                  </Table.Td>
                  <Table.Td>
                    <LineStatus line={line} />
                  </Table.Td>
                  <Table.Td ta="right">{line.reason ? money(line.discountAmount) : '—'}</Table.Td>
                  <Table.Td ta="right">{line.reason ? money(line.netAmount) : '—'}</Table.Td>
                  <Table.Td ta="right">{line.overage > 0 ? money(line.overage) : '—'}</Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  );
}

function ActivityTab({ contract }: Readonly<{ contract: ContractDetail }>) {
  const { monitoredSubjects, batches } = contract.activity;
  return (
    <Stack>
      <Text size="sm" c="dimmed">
        Work in this window with no price of its own yet. It is counted here and does not draw down.
      </Text>
      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        <StatTile label="Subjects monitored" value={String(monitoredSubjects)} />
        <StatTile
          label="Screening-batch reports"
          value={String(batches.reduce((sum, batch) => sum + batch.reports, 0))}
          note={batches.length ? `across ${batches.length} batch(es)` : undefined}
        />
      </SimpleGrid>
      {batches.length > 0 ? (
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Batch</Table.Th>
              <Table.Th ta="right">Reports completed</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {batches.map((batch) => (
              <Table.Tr key={batch.batchId}>
                <Table.Td>{batch.label}</Table.Td>
                <Table.Td ta="right">{batch.reports}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      ) : null}
    </Stack>
  );
}

function ReconciliationTab({ contract }: Readonly<{ contract: ContractDetail }>) {
  const { reconciliation, isRunning, backofficeUnreadable, error, run } = useContractReconciliation(
    contract.id
  );
  const money = (value: number | null, currency: string | null) =>
    value === null ? '—' : (formatMoney(value, currency) ?? String(value));

  return (
    <Stack>
      <Group justify="space-between" align="flex-start">
        <Text size="sm" c="dimmed" maw={640}>
          The Hub&apos;s figures for each project against Backoffice&apos;s, which are still the
          ones invoiced. Reads Backoffice live.
        </Text>
        <Button onClick={() => void run()} loading={isRunning}>
          {reconciliation ? 'Run again' : 'Run reconciliation'}
        </Button>
      </Group>
      {backofficeUnreadable ? (
        <Alert color="yellow" title="Backoffice could not be read">
          {backofficeUnreadable}. The figures on this page are unaffected; only the comparison is
          unavailable.
        </Alert>
      ) : null}
      {error ? (
        <Alert color="red" title="Reconciliation failed">
          {error.message}
        </Alert>
      ) : null}
      {reconciliation ? (
        <>
          <Group gap="xs">
            {(Object.entries(reconciliation.counts) as [ReconClass, number][]).map(
              ([cls, count]) => (
                <Badge key={cls} variant="light" color={SEVERITY_COLOR[reconSeverity(cls)]}>
                  {RECON_CLASS_LABELS[cls]}: {count}
                </Badge>
              )
            )}
          </Group>
          <Text size="sm">
            Hub consumed {money(reconciliation.hubConsumed, contract.currency)}; Backoffice&apos;s
            cached usage {money(reconciliation.djangoGrossUsage, contract.currency)}.
          </Text>
          {reconciliation.rows.length === 0 ? (
            <Text size="sm" c="dimmed" ta="center" py="lg">
              No projects in this contract on either side.
            </Text>
          ) : (
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Project</Table.Th>
                  <Table.Th>Result</Table.Th>
                  <Table.Th ta="right">Hub price</Table.Th>
                  <Table.Th ta="right">Backoffice price</Table.Th>
                  <Table.Th ta="right">Hub discount</Table.Th>
                  <Table.Th ta="right">Backoffice discount</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {reconciliation.rows.map((row) => (
                  <Table.Tr key={row.hubItem ?? `project-${row.projectId}`}>
                    <Table.Td>
                      <Text size="sm">
                        {row.projectId ? `Project ${row.projectId}` : 'No Backoffice project'}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {row.completedDate.slice(0, 10)}
                        {row.projectCode ? ` · ${row.projectCode}` : ''}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        size="sm"
                        variant="light"
                        color={SEVERITY_COLOR[reconSeverity(row.classification)]}
                      >
                        {RECON_CLASS_LABELS[row.classification]}
                      </Badge>
                      {row.note ? (
                        <Text size="xs" c="dimmed" maw={360}>
                          {row.note}
                        </Text>
                      ) : null}
                      {row.partnerFeesMatch === false ? (
                        <Text size="xs" c="dimmed">
                          Partner fees differ: Hub {money(row.hubPartnerFees, row.djangoCurrency)},
                          Backoffice {money(row.djangoPartnerFees, row.djangoCurrency)}
                        </Text>
                      ) : null}
                    </Table.Td>
                    <Table.Td ta="right">{money(row.hubAmount, row.hubCurrency)}</Table.Td>
                    <Table.Td ta="right">
                      {money(row.djangoFinalPrice, row.djangoCurrency)}
                    </Table.Td>
                    <Table.Td ta="right">{money(row.hubDiscount, row.hubCurrency)}</Table.Td>
                    <Table.Td ta="right">{money(row.djangoDiscount, row.djangoCurrency)}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </>
      ) : null}
    </Stack>
  );
}

/**
 * Admin: one client contract — what has been drawn from its commitment, the
 * discount on each item, the work in its window that does not draw down, and
 * how the Hub's figures compare with Backoffice's.
 */
export function UsageContractPage() {
  const { contractId } = useParams<{ contractId: string }>();
  const { contract, isLoading, error } = useClientContract(contractId);

  const crossing = useMemo(
    () => contract?.lines.find((line) => line.itemId === contract.crossingItemId) ?? null,
    [contract]
  );

  if (isLoading && !contract) {
    return (
      <Center h={300}>
        <Loader />
      </Center>
    );
  }
  if (error || !contract) {
    return (
      <Container size="xl" py="md">
        <Text c="red">Could not load the contract{error ? `: ${error.message}` : ''}</Text>
      </Container>
    );
  }

  const money = (value: number) => formatMoney(value, contract.currency) ?? String(value);
  const over = contract.consumed > contract.commitmentAmount;
  const crossingAt =
    crossing && crossing.remainingBefore !== null
      ? contract.commitmentAmount - crossing.remainingBefore
      : null;

  return (
    <Container size="xl" py="md">
      <Stack>
        <Anchor component={Link} to="/admin/usage" size="sm">
          <Group gap={4}>
            <IconArrowLeft size={14} />
            All contracts
          </Group>
        </Anchor>
        <Stack gap={4}>
          <PageTitle title={contract.clientName ?? `Team ${contract.teamId}`} />
          <Text size="sm" c="dimmed">
            Contract {contract.backofficeContractId} · {contract.startsOn} to {contract.endsOn} ·{' '}
            {percentText(contract.discountRate)} discount
            {contract.exceptionalCircumstances ? ' throughout (exceptional circumstances)' : ''}
          </Text>
        </Stack>

        {contract.currency === null ? (
          <Alert color="red" title="No currency">
            Backoffice holds no currency for this contract, so nothing can draw down against it.
          </Alert>
        ) : null}
        {contract.overlappingContractIds.length > 0 ? (
          <Alert color="orange" title="Overlapping contract">
            Another contract of this team covers some of the same days. Backoffice applies one of
            them arbitrarily; the reconciliation shows which.
          </Alert>
        ) : null}

        <SimpleGrid cols={{ base: 2, md: 5 }}>
          <StatTile label="Committed" value={money(contract.commitmentAmount)} />
          <StatTile label="Drawn" value={money(contract.consumed)} />
          <StatTile
            label={over ? 'Over commitment' : 'Remaining'}
            value={money(over ? contract.overage : contract.remaining)}
          />
          <StatTile label="Discount given" value={money(contract.discount)} />
          <StatTile label="Net billable" value={money(contract.net)} />
        </SimpleGrid>

        <LogoPaper>
          <Stack gap="xs">
            <ContractMeter
              commitment={contract.commitmentAmount}
              consumed={contract.consumed}
              currency={contract.currency}
              crossingAt={crossingAt}
            />
            {crossing ? (
              <Tooltip
                label="Backoffice gives the crossing item the full discount, and so does the Hub."
                withArrow
              >
                <Text size="xs" c="dimmed">
                  {crossing.label ?? `Project ${crossing.sourceRef}`} on{' '}
                  {crossing.consumedAt.slice(0, 10)} took consumption past the commitment, and was
                  still discounted in full.
                </Text>
              </Tooltip>
            ) : null}
            {Object.entries(contract.otherCurrencyTotals).map(([code, total]) => (
              <Text key={code} size="xs" c="dimmed">
                {formatMoney(total, code)} of work billed in {code} does not draw down this
                contract.
              </Text>
            ))}
          </Stack>
        </LogoPaper>

        <Tabs defaultValue="lines">
          <Tabs.List mb="sm">
            <Tabs.Tab value="lines">Line items</Tabs.Tab>
            <Tabs.Tab value="activity">Unpriced work</Tabs.Tab>
            <Tabs.Tab value="reconciliation">Reconciliation</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="lines">
            <LogoPaper>
              <LinesTab contract={contract} />
            </LogoPaper>
          </Tabs.Panel>
          <Tabs.Panel value="activity">
            <LogoPaper>
              <ActivityTab contract={contract} />
            </LogoPaper>
          </Tabs.Panel>
          <Tabs.Panel value="reconciliation">
            <LogoPaper>
              <ReconciliationTab contract={contract} />
            </LogoPaper>
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Container>
  );
}
